import { createReducer, on } from '@ngrx/store';
import {
  ILocation,
  ILocationListFilter,
  ILocationListSort,
  ILocationName,
} from '../../models/location.model';
import { LocationActions } from './location.actions';
import { AuthActions } from '../auth/auth.actions';

export interface LocationState {
  locations: ILocation[];
  currentLocation: ILocation | null;
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
  filter: ILocationListFilter;
  sort: ILocationListSort;
  saveSuccess: boolean;
  saveFailure: boolean;
  locationNames?: ILocationName[];
  allStatusLocationNames?: ILocationName[];
}

export const initialLocationState: LocationState = {
  locations: [],
  currentLocation: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
  },
  filter: {},
  sort: {
    key: 'createdAt', // Or a relevant default sort key for locations
    order: 'desc',
  },
  saveSuccess: false,
  saveFailure: false,
  locationNames: [],
  allStatusLocationNames: [],
};

export const locationReducer = createReducer<LocationState>(
  initialLocationState,
  // Load Locations
  on(LocationActions.loadLocations, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(LocationActions.loadLocationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(LocationActions.loadLocationsSuccess, (state, { response, filter }) => ({
    ...state,
    locations: response.data.locations.list,
    pagination: {
      total: response.data.locations.total,
      page: response.data.locations.page,
      limit: response.data.locations.limit,
    },
    filter: filter ?? {},
    loading: false,
    error: null,
  })),

  // Create Location
  on(LocationActions.createLocation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(LocationActions.createLocationSuccess, (state, { response }) => ({
    ...state,
    locations: [...state.locations, response.data],
    loading: false,
    error: null,
  })),

  on(LocationActions.createLocationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Single Location
  on(LocationActions.loadSingleLocation, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(LocationActions.loadSingleLocationSuccess, (state, { response }) => ({
    ...state,
    currentLocation: response.data,
    loading: false,
    error: null,
  })),

  on(LocationActions.loadSingleLocationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Update Location
  on(LocationActions.updateLocationSuccess, (state) => ({
    ...state,
    currentLocation: null,
    loading: false,
    error: null,
  })),

  // Delete Location
  on(LocationActions.deleteLocationSuccess, (state, { id }) => ({
    ...state,
    locations: state.locations.filter((loc) => loc._id !== id),
    loading: false,
    error: null,
  })),

  // Toggle Status
  on(LocationActions.toggleLocationStatusSuccess, (state, { response }) => ({
    ...state,
    locations: state.locations.map((loc) =>
      loc._id === response.data.updatedLocation._id
        ? response.data.updatedLocation
        : loc
    ),
    currentLocation:
      state.currentLocation?._id === response.data.updatedLocation._id
        ? response.data.updatedLocation
        : state.currentLocation,
    loading: false,
    error: null,
  })),
  on(LocationActions.setLocationQueryParams, (state, { params, body }) => ({
    ...state,
    pagination: {
      ...state.pagination,
      page: params.page ?? state.pagination.page,
      limit: params.limit ?? state.pagination.limit,
    },
    filter: body?.filter ?? state.filter,
    sort: body?.sort ?? state.sort,
  })),
  // Load Location Names
  on(LocationActions.loadLocationNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(LocationActions.loadLocationNamesSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    locationNames: response.data.locations || [],
  })),
  on(LocationActions.loadLocationNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load AllStatus Location Names
  on(LocationActions.loadAllStatusLocationNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(
    LocationActions.loadAllStatusLocationNamesSuccess,
    (state, { response }) => ({
      ...state,
      loading: false,
      allStatusLocationNames: response.data.locations || [],
    })
  ),
  on(LocationActions.loadAllStatusLocationNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // update status
  on(LocationActions.changeLocationStatus, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(LocationActions.changeLocationStatusSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(LocationActions.changeLocationStatusFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(LocationActions.clearSingleLocation, (state) => ({
    ...state,
    currentLocation: null,
  })),
  on(AuthActions.logout, () => initialLocationState)
);
